import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { BracketShareCard, BRACKET_SHARE_CARD_WIDTH } from '@/components/share/BracketShareCard';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { OutBadge } from '@/components/wc/OutBadge';
import { useThemePalette } from '@/config/theme';
import { useWcEliminatedTeams } from '@/features/wc/eliminations';
import {
  allocateThirds,
  buildR32,
  GROUP_KEYS,
  type GroupKey,
  type Qualifiers,
  resolveTies,
  ROUND_MATCHES,
  type RoundKey,
} from '@/features/predictor/bracket';
import { useMyBracket, useMyBracketStats } from '@/features/predictor/hooks';
import { useCompetitions } from '@/features/leagues/hooks';
import { isSupabaseConfigured } from '@/config';
import { listCompetitionSeasonFixtures, type FixtureWithRefs, type TeamRef } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { captureAndShare } from '@/lib/share/captureAndShare';

const WC_PROVIDER_ID = 9900001;
const WC_SEASON = 2026;
const APP_LINK = 'https://play.google.com/store/apps/details?id=com.tashkeela.app';

/** Knockout round labels API-Football emits (matched loosely, case-insensitive). */
function isKnockoutRound(round: string | null): boolean {
  if (!round) return false;
  return /round of|1\/8|1\/16|quarter|semi|final/i.test(round) && !/group/i.test(round);
}

const ROUND_TITLE: Record<RoundKey, string> = {
  R32: 'bracketLive.r32',
  R16: 'bracketLive.r16',
  QF: 'bracketLive.qf',
  SF: 'bracketLive.sf',
  F: 'bracketLive.final',
};
const SHOWN_ROUNDS: RoundKey[] = ['R16', 'QF', 'SF', 'F'];

type TeamLookup = Map<string, TeamRef>;

/** The actual winner of a finished knockout fixture (null while undecided / drawn). */
function fixtureWinnerId(f: FixtureWithRefs): string | null {
  if (f.status !== 'finished' || f.home_score == null || f.away_score == null) return null;
  if (f.home_score > f.away_score) return f.home_team?.id ?? null;
  if (f.away_score > f.home_score) return f.away_team?.id ?? null;
  return null; // level on score (penalties not in the score columns) - leave undecided
}

function TeamSide({
  teamId,
  lookup,
  picked,
  correct,
}: {
  teamId: string | null;
  lookup: TeamLookup;
  picked: boolean;
  /** true = pick confirmed right, false = wrong, null = not decided yet. */
  correct: boolean | null;
}) {
  const palette = useThemePalette();
  const { locale } = useLocale();
  const team = teamId ? lookup.get(teamId) ?? null : null;
  return (
    <View className={`flex-row items-center gap-2 rounded-lg px-1.5 py-1 ${picked ? 'bg-volt/10' : ''}`}>
      <TeamCrest uri={team?.logo_url} name={team ? localizeTeamName(team, locale) : 'TBD'} size="sm" />
      <AppText
        variant="caption"
        weight={picked ? 'bold' : 'medium'}
        className={`flex-1 ${picked ? 'text-ink' : 'text-soft'}`}
        numberOfLines={1}
      >
        {team ? localizeTeamName(team, locale, true) : '—'}
      </AppText>
      {picked && correct === true ? (
        <Ionicons name="checkmark-circle" size={14} color={palette.voltBright} />
      ) : picked && correct === false ? (
        <Ionicons name="close-circle" size={14} color={palette.loss} />
      ) : picked ? (
        <Ionicons name="ellipse" size={8} color={palette.volt} />
      ) : null}
    </View>
  );
}

export default function BracketLiveScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const palette = useThemePalette();
  const howto = useHowToPlay('bracketLive');

  const bracket = useMyBracket();
  const bracketStats = useMyBracketStats();
  const competitions = useCompetitions();
  const wcId = (competitions.data ?? []).find((c) => c.provider_id === WC_PROVIDER_ID)?.id;
  const fixtures = useQuery({
    queryKey: ['wc-fixtures', wcId],
    queryFn: () => listCompetitionSeasonFixtures(wcId as string, WC_SEASON),
    enabled: isSupabaseConfigured && Boolean(wcId),
  });

  const lookup: TeamLookup = useMemo(() => {
    const map: TeamLookup = new Map();
    for (const f of fixtures.data ?? []) {
      if (f.home_team?.id) map.set(f.home_team.id, f.home_team);
      if (f.away_team?.id) map.set(f.away_team.id, f.away_team);
    }
    return map;
  }, [fixtures.data]);

  const koFixtures = useMemo(
    () => (fixtures.data ?? []).filter((f) => isKnockoutRound(f.round)),
    [fixtures.data],
  );

  // Resolve the caller's predicted bracket from their saved payload.
  const resolved = useMemo(() => {
    const payload = bracket.data;
    if (!payload?.groups) return null;
    const groups = payload.groups as Record<string, string[]>;
    const groupOf = (teamId: string): GroupKey | undefined =>
      GROUP_KEYS.find((k) => (groups[k] ?? []).includes(teamId));
    const qualifiers: Qualifiers = { winner: {}, runnerUp: {}, third: {} };
    for (const k of GROUP_KEYS) {
      const o = groups[k];
      if (o?.[0]) qualifiers.winner[k] = o[0];
      if (o?.[1]) qualifiers.runnerUp[k] = o[1];
      if (o?.[2]) qualifiers.third[k] = o[2];
    }
    const picks: Record<number, string | null> = {};
    for (const [k, v] of Object.entries(payload.picks ?? {})) picks[Number(k)] = v as string | null;
    const thirdBySlot = allocateThirds((payload.thirds as string[]) ?? [], groupOf);
    const ties = resolveTies(buildR32(qualifiers, thirdBySlot), picks);
    return { ties, picks, champion: (payload.champion as string | null) ?? null };
  }, [bracket.data]);

  // Match a predicted tie to a real knockout fixture by its pair of teams. Skips
  // fixtures with a null team id so the score-orientation swap below is always safe.
  const findFixture = useCallback(
    (homeId: string | null, awayId: string | null): FixtureWithRefs | undefined => {
      if (!homeId || !awayId) return undefined;
      return koFixtures.find((f) => {
        const h = f.home_team?.id;
        const a = f.away_team?.id;
        if (h == null || a == null) return false;
        return (h === homeId || a === homeId) && (h === awayId || a === awayId);
      });
    },
    [koFixtures],
  );

  // Live tally: how many of the caller's knockout picks have come true so far.
  const tally = useMemo(() => {
    if (!resolved) return { correct: 0, decided: 0 };
    let correct = 0;
    let decided = 0;
    for (const round of SHOWN_ROUNDS) {
      for (const id of ROUND_MATCHES[round]) {
        const tie = resolved.ties[id];
        const pick = resolved.picks[id];
        if (!tie || !pick) continue;
        const fx = findFixture(tie.home, tie.away);
        const winner = fx ? fixtureWinnerId(fx) : null;
        if (winner) {
          decided += 1;
          if (winner === pick) correct += 1;
        }
      }
    }
    return { correct, decided };
  }, [resolved, findFixture]);

  const championTeam = resolved?.champion ? lookup.get(resolved.champion) ?? null : null;
  const { isEliminated } = useWcEliminatedTeams();
  const championOut = isEliminated(resolved?.champion);
  const loading = bracket.isPending || competitions.isPending;

  // Share: rasterise the off-screen branded bracket card (or fall back to text).
  const shareRef = useRef<View>(null);
  const [sharing, setSharing] = useState(false);
  const stats = bracketStats.data;
  const shareData =
    resolved && (championTeam || stats)
      ? {
          championName: championTeam
            ? localizeTeamName(championTeam, locale, true)
            : t('bracketShare.noChampion'),
          championLogo: championTeam?.logo_url ?? null,
          points: stats?.points ?? 0,
          rank: stats?.rank ?? 0,
        }
      : null;

  const onShare = async () => {
    if (!shareData) return;
    setSharing(true);
    try {
      const n = (x: number) => formatNumber(x, numeralOptions);
      const headline =
        shareData.rank > 0
          ? t('bracketShare.shareLineRank', {
              champion: shareData.championName,
              rank: n(shareData.rank),
            })
          : t('bracketShare.shareLineChampion', { champion: shareData.championName });
      const message = `${headline}\n${t('common.viaApp')} ${APP_LINK}`;
      await captureAndShare({ viewRef: shareRef, message, dialogTitle: t('bracketShare.kicker') });
    } finally {
      setSharing(false);
    }
  };

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="bracketLive" icon="git-network" />
      <BackHeader title={t('bracketLive.title')} right={<HowToPlayButton onPress={howto.open} />} />
      {bracket.isError || competitions.isError ? (
        <ErrorState
          error={bracket.error ?? competitions.error}
          onRetry={() => {
            void bracket.refetch();
            void competitions.refetch();
          }}
        />
      ) : loading ? (
        <View className="gap-3 px-4 pt-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </View>
      ) : !resolved ? (
        <View className="flex-1 justify-center px-6">
          <EmptyState
            icon="git-network-outline"
            title={t('bracketLive.noBracketTitle')}
            body={t('bracketLive.noBracketBody')}
          />
          <View className="mt-4 px-6">
            <Button label={t('bracketLive.makeBracket')} onPress={() => router.push('/predictor')} />
          </View>
        </View>
      ) : (
        <ScrollView contentContainerStyle={{ paddingBottom: 40, gap: 12 }}>
          {/* Champion + live tally */}
          <Card
            className="mx-4 mt-1 flex-row items-center gap-3 px-4 py-3.5"
            style={championOut ? { opacity: 0.6 } : undefined}
          >
            <View className="h-14 w-14 items-center justify-center rounded-full bg-volt-glass">
              {championTeam ? (
                <TeamCrest uri={championTeam.logo_url} name={localizeTeamName(championTeam, locale)} size="lg" />
              ) : (
                <Ionicons name="trophy" size={24} color={palette.voltBright} />
              )}
            </View>
            <View className="flex-1">
              <View className="flex-row items-center gap-1.5">
                <AppText variant="micro" className="uppercase tracking-wide text-muted">
                  {t('bracketLive.yourChampion')}
                </AppText>
                {championOut ? <OutBadge compact /> : null}
              </View>
              <AppText
                variant="caption"
                weight="bold"
                className="text-ink"
                numberOfLines={1}
                style={championOut ? { textDecorationLine: 'line-through' } : undefined}
              >
                {championTeam ? localizeTeamName(championTeam, locale, true) : t('bracketLive.noChampion')}
              </AppText>
              <AppText variant="micro" className="text-soft">
                {t('bracketLive.tally', {
                  correct: formatNumber(tally.correct, numeralOptions),
                  decided: formatNumber(tally.decided, numeralOptions),
                })}
              </AppText>
            </View>
          </Card>

          {/* Your contest rank · points — tap through to the bracket leaderboard. */}
          {stats ? (
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push('/games/leaderboards-hub?tab=bracket' as never)}
              className="mx-4 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3 active:opacity-90"
            >
              <View className="h-9 w-9 items-center justify-center rounded-full bg-volt-glass-strong">
                <Ionicons name="podium" size={18} color={palette.voltBright} />
              </View>
              <AppText variant="caption" weight="bold" className="flex-1 text-ink" numberOfLines={1}>
                {t('predictor.rankStat', {
                  rank: `#${formatNumber(stats.rank, numeralOptions)}`,
                  points: formatNumber(stats.points, numeralOptions),
                })}
              </AppText>
              <Ionicons name="chevron-forward" size={18} color={palette.voltBright} />
            </Pressable>
          ) : null}

          {/* Share my bracket (champion + score + rank). */}
          {shareData ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('bracketShare.action')}
              disabled={sharing}
              onPress={onShare}
              className="mx-4 flex-row items-center justify-center gap-2 rounded-card border border-volt-line bg-volt-glass py-3 active:bg-volt-glass-strong disabled:opacity-50"
            >
              <Ionicons
                name={sharing ? 'hourglass-outline' : 'share-social'}
                size={16}
                color={palette.voltBright}
              />
              <AppText variant="caption" weight="bold" className="text-volt-bright">
                {t('bracketShare.action')}
              </AppText>
            </Pressable>
          ) : null}

          {koFixtures.length === 0 ? (
            <View className="mx-4 flex-row items-center gap-2 rounded-card border border-dashed border-line px-4 py-3">
              <Ionicons name="time-outline" size={16} color={palette.muted} />
              <AppText variant="micro" className="flex-1 text-muted">
                {t('bracketLive.preKnockout')}
              </AppText>
            </View>
          ) : null}

          {/* Rounds: each predicted tie with live result overlay */}
          {SHOWN_ROUNDS.map((round) => (
            <View key={round} className="gap-2">
              <AppText variant="caption" weight="bold" className="mx-4 uppercase tracking-wide text-soft">
                {t(ROUND_TITLE[round])}
              </AppText>
              <Card className="mx-4 overflow-hidden">
                {ROUND_MATCHES[round].map((id, i) => {
                  const tie = resolved.ties[id];
                  const pick = resolved.picks[id] ?? null;
                  const fx = findFixture(tie?.home ?? null, tie?.away ?? null);
                  const winner = fx ? fixtureWinnerId(fx) : null;
                  const correctFor = (teamId: string | null): boolean | null =>
                    !pick || pick !== teamId ? null : winner == null ? null : winner === pick;
                  return (
                    <View key={id} className={`gap-1 px-3 py-2 ${i > 0 ? 'border-t border-line' : ''}`}>
                      <View className="flex-row items-center justify-between">
                        <View className="flex-1">
                          <TeamSide teamId={tie?.home ?? null} lookup={lookup} picked={pick === tie?.home} correct={correctFor(tie?.home ?? null)} />
                          <TeamSide teamId={tie?.away ?? null} lookup={lookup} picked={pick === tie?.away} correct={correctFor(tie?.away ?? null)} />
                        </View>
                        {fx && fx.home_score != null && fx.away_score != null ? (
                          <View className="ml-2 items-center">
                            <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
                              {/* Bidi-isolate so the digit pair can't visually swap in Arabic RTL. */}
                              {`⁦${formatNumber(
                                fx.home_team?.id === tie?.home ? fx.home_score : fx.away_score,
                                numeralOptions,
                              )} - ${formatNumber(
                                fx.home_team?.id === tie?.home ? fx.away_score : fx.home_score,
                                numeralOptions,
                              )}⁩`}
                            </AppText>
                            <AppText variant="micro" className="text-muted">
                              {fx.status === 'live' ? t('common.live') : t('match.fullTime')}
                            </AppText>
                          </View>
                        ) : null}
                      </View>
                    </View>
                  );
                })}
              </Card>
            </View>
          ))}

          {/* Off-screen branded card we rasterise to a PNG for the share sheet.
              Positioned far off-canvas so it never shows; pointer-inert. */}
          {shareData ? (
            <View
              style={{ position: 'absolute', left: -9999, top: 0, width: BRACKET_SHARE_CARD_WIDTH }}
              pointerEvents="none"
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            >
              <BracketShareCard ref={shareRef} data={shareData} />
            </View>
          ) : null}
        </ScrollView>
      )}
    </Screen>
  );
}
